import React, { useEffect, useRef, useState } from 'react';
import { ThemeProvider, useMediaQuery } from '@material-ui/core';
import globalMaterialUITheme from '@alo/yoga-ui';
import SearchResultsModal from './SearchResultsModal';
import { trackRecentSearch } from './utils';
import { observer } from 'mobx-react-lite';
import { SearchState } from './state';
import { useSpring, animated } from 'react-spring';
import { autorun } from 'mobx';
import { MUI_MOBILE_BREAKPOINT } from '../../../utils/constants';
import { closeDesktopSearch, closeMobileSearch } from '../initializeHeaderScripts';
import { prependLocaleToPath } from '../../../utils/localization';
import { productServiceClient } from 'src/scripts/templates/_shared/apolloClientUtils';
import { ApolloProvider } from '@apollo/client';

let SearchForm = ({ desktopVersionOnTablet = false }: { desktopVersionOnTablet?: boolean }) => {
  const [searchText, setSearchText] = useState('');
  const [open, setOpen] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const _isMobileAndTablet = useMediaQuery('(max-width: 1059.98px)', { noSsr: true });
  const _isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true });

  const isMobileOrTablet = desktopVersionOnTablet ? _isMobile : _isMobileAndTablet;

  const animationStyles = useSpring({
    to: {
      right: SearchState.isOpen ? '50%' : '0%',
      opacity: SearchState.isOpen ? 1 : 0,
    },
    from: {
      right: '0%',
      opacity: 0,
    },
    config: { duration: 150 },
    onRest: () => {
      if (!isMobileOrTablet) setShowSuggestions(SearchState.isOpen);
    },
  });

  const input = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    autorun(() => {
      setOpen(SearchState.isOpen);
      if (SearchState.isOpen) {
        setSearchText('');
        if (isMobileOrTablet) setShowSuggestions(true);
      }
    });
  }, [isMobileOrTablet]);

  useEffect(() => {
    if (open) {
      const ref = input?.current;
      ref?.focus();
    }
  }, [showSuggestions, isMobileOrTablet, open]);

  if (!SearchState.isOpen) return null;

  return (
    <ThemeProvider theme={globalMaterialUITheme}>
      <animated.div
        style={
          !isMobileOrTablet
            ? {
                ...animationStyles,
              }
            : {}
        }
      >
        <div
          id={isMobileOrTablet ? 'searchMobile' : 'search'}
          className={
            isMobileOrTablet
              ? 'alo-search-drawer position-relative'
              : 'd-md-flex main-nav__search search-form justify-content-center align-items-center flex-column'
          }
        >
          <div className="d-flex w-100 justify-content-center align-items-center mobile-search-drawer-top-portion">
            {/* Back arrow close button for mobile */}
            {isMobileOrTablet && (
              <button
                id="mobile-search-button"
                className={`searchform-close back-arrow ${isInputFocused ? '' : 'unfocused-input'}`}
                type="button"
                onClick={closeMobileSearch}
              >
                <span className="alo-chevron-left-icon"></span>
              </button>
            )}
            <form
              className={`search-input-container d-flex justify-content-center align-items-center ${
                isInputFocused ? '' : 'unfocused-input'
              }`}
              action="/search"
              method="get"
              role="search"
              onSubmit={(event) => onSubmit(event, searchText)}
              autoComplete="off"
            >
              <span className="search-icon active">
                <span className="alo-search-icon"></span>
              </span>
              <input
                ref={input}
                type="search"
                name="q"
                className={
                  isMobileOrTablet
                    ? 'main-nav__search-input main-nav__search-input--mobile'
                    : 'yoga-ui-input main-nav-search-input-web'
                }
                placeholder="Search"
                id="SearchInput"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onFocus={() => setIsInputFocused(true)} // Set focus state to true when input is focused
                onBlur={() => setIsInputFocused(false)}
                autoFocus
                onKeyDown={(e) => {
                  // Navigate to first suggestion with arrow key
                  // ? [REVISIT] Smelly workaround to avoid rebuilding a whole search component. https://alodigital.atlassian.net/browse/ECOM-1818
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    const nextLink = [...document.querySelectorAll('.search-link')][0];
                    nextLink && nextLink.focus();
                  }
                }}
              />
              <button
                className="searchform-reset lowercase-button"
                type="reset"
                aria-label="Clear Search"
                style={{ visibility: searchText ? 'visible' : 'hidden' }}
                onClick={() => {
                  setSearchText('');
                }}
              >
                <span aria-hidden="true">Clear</span>
              </button>
            </form>
          </div>
          {/* Close button for desktop */}
          {!isMobileOrTablet && (
            <button
              id="main-navbar-search-close-icon"
              className="searchform-close close-icon"
              type="button"
              onClick={closeDesktopSearch}
            >
              <span className="alo-cross-primary-icon"></span>
            </button>
          )}
          <ApolloProvider client={productServiceClient}>
            {showSuggestions && (
              <SearchResultsModal searchText={searchText} isMobileVersion={isMobileOrTablet}></SearchResultsModal>
            )}
          </ApolloProvider>
        </div>
      </animated.div>
    </ThemeProvider>
  );
};

SearchForm = observer(SearchForm);
export { SearchForm };

const onSubmit = (event: React.FormEvent<HTMLFormElement>, searchText: string) => {
  event.preventDefault();
  const trimmedSearchText = searchText.trim().toLowerCase();
  if (trimmedSearchText.length < 1) {
    return;
  }
  trackRecentSearch(trimmedSearchText);
  window.dataLayer.push({ bloomreach_search_term: trimmedSearchText });
  window.dataLayer.push({ event: 'bloomreach_search_event' });
  window.location.href = prependLocaleToPath(`/search?q=${encodeURIComponent(trimmedSearchText)}`);
  // optimizely
  window['optimizely'] = window['optimizely'] || [];
  window['optimizely'].push({
    type: 'event',
    eventName: 'search_event',
    tags: {
      search_term: trimmedSearchText,
    },
  });
};
